import React from 'react';
import { Alert, Box, Grid, Stack } from '@mui/material';
import { useEnergy } from '../context/EnergyContext';
import { signedPowerLabel } from '../utils/energyViewModel';
import { EnergyLineChart, EnergyPageHeader, MetricTile, ProvenanceNote, SectionCard } from '../components/energy/EnergyUi';

export default function PccView() {
  const { model } = useEnergy();
  const { pcc } = model;
  return (
    <Box>
      <EnergyPageHeader title="Utility Meter / PCC" subtitle="Bidirectional exchange, revenue-grade meter state, power quality, and history" back />
      <Grid container spacing={2}>
        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <SectionCard title="Interconnection exchange">
              <MetricTile label="Current grid power" value={pcc.powerKw} unit="kW" accent="#1976d2" state={signedPowerLabel(pcc.powerKw, 'Importing', 'Exporting')} provenance="Metered" />
              <Grid container spacing={1} sx={{ mt: 0.25 }}>
                <Grid item xs={6}><MetricTile label="Selected period imported" value={pcc.importedKwh} measure={model.measures?.energy?.importedKwh} unit="kWh" accent="#1976d2" /></Grid>
                <Grid item xs={6}><MetricTile label="Selected period exported" value={pcc.exportedKwh} measure={model.measures?.energy?.exportedKwh} unit="kWh" accent="#0288d1" /></Grid>
              </Grid>
              <ProvenanceNote>Import and export use exact hardware-counter evidence when complete, with separately identified power estimates and coverage.</ProvenanceNote>
            </SectionCard>
            <SectionCard title="Power quality">
              <Grid container spacing={1}>
                <Grid item xs={6}><MetricTile label="Line voltage" value={pcc.voltageV} unit="V" /></Grid>
                <Grid item xs={6}><MetricTile label="Line current" value={pcc.currentA} unit="A" /></Grid>
                <Grid item xs={6}><MetricTile label="Frequency" value={pcc.frequencyHz} unit="Hz" /></Grid>
                <Grid item xs={6}><MetricTile label="Power factor" value={pcc.powerFactor} /></Grid>
              </Grid>
              <Alert severity={pcc.communicationState ? 'info' : 'warning'} sx={{ mt: 1.5 }}>
                Communication: {pcc.communicationState || '-'} · Meter quality: {pcc.meterQuality || '-'}
              </Alert>
            </SectionCard>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={8}>
          <Stack spacing={2}>
            <SectionCard title="Utility exchange with site context">
              <EnergyLineChart series={model.series} fields={[{ key: 'gridKw', label: 'Grid import / export' }, { key: 'loadKw', label: 'Load' }, { key: 'solarKw', label: 'Solar' }, { key: 'bessKw', label: 'Battery' }]} height={300} />
            </SectionCard>
            <SectionCard title="Electrical compatibility">
              <Grid container spacing={1}>
                <Grid item xs={6}><MetricTile label="Voltage difference" value={pcc.electrical?.voltageDifferenceV} unit="V" state={pcc.electrical?.status} /></Grid>
                <Grid item xs={6}><MetricTile label="Frequency difference" value={pcc.electrical?.frequencyDifferenceHz} unit="Hz" state={pcc.electrical?.status} /></Grid>
              </Grid>
              <ProvenanceNote>Matching voltage and frequency alone do not prove phase synchronization. Synchronization: {pcc.electrical?.electricalSynchronization || 'unknown'}.</ProvenanceNote>
            </SectionCard>
            <SectionCard title="Reconciliation">
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}><MetricTile label="Residual" value={model.power.residualKw} unit="kW" accent={model.power.balanced === false ? '#ed6c02' : '#388e3c'} /></Grid>
                <Grid item xs={12} sm={4}><MetricTile label="Tolerance" value={model.power.toleranceKw} unit="kW" /></Grid>
                <Grid item xs={12} sm={4}><MetricTile label="Coverage" value={model.meta.coveragePct} unit="%" /></Grid>
              </Grid>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
